import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDown, Search } from 'lucide-react';

import { messageErreur } from '../../lib/erreurs';
import { formatMontant, formatNombre } from '../../lib/formats';
import { cn } from '../../lib/utils';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { BarrePagination } from '../ui/pagination-bar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LoadingState } from '../ui/loading-state';

/**
 * Tableau des soldes d'un genre de tiers (clients, chauffeurs, fournisseurs),
 * lu par pages du serveur, comme une feuille du classeur: colonnes nommées,
 * montants alignés à droite, ligne de total sur toutes les lignes retenues
 * (pas seulement la page), tri par clic sur l'en-tête. Recherche, filtre,
 * tri et page vivent dans l'adresse (`clients-q`, `clients-tout`, …).
 *
 * `colonnes`: `{ cle, titre, montant, tri, masquee(totaux), rendu(ligne) }`.
 * Les colonnes `montant` lisent `ligne[cle]` et `totaux[cle]`.
 */
export default function TableauSoldes({
  cle,
  charger,
  colonnes,
  identifiant,
  nom,
  detailMobile,
  montantMobile,
  filtre,
  triParDefaut,
  action,
  onOuvrir,
  rechercheLibelle,
  totalLibelle,
  domaines,
  barre,
  rafraichir = 0,
  vide,
  testid,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const recherche = searchParams.get(`${cle}-q`) || '';
  const termes = useDebouncedValue(recherche, 350);
  const filtreActif = filtre ? searchParams.get(`${cle}-tout`) !== '1' && (filtre.actifParDefaut ?? true) : false;
  const tri = searchParams.get(`${cle}-tri`) || triParDefaut;
  const changer = (nomParam, valeur, remplacer = false) => setSearchParams((precedent) => {
    const suivant = new URLSearchParams(precedent);
    if (valeur) suivant.set(nomParam, valeur);
    else suivant.delete(nomParam);
    return suivant;
  }, { replace: remplacer });

  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const pagination = usePaginationServeur(donnees?.total, {
    cle,
    reinitialiser: `${termes}|${filtreActif}|${tri}`,
    taillePrefere: 50,
  });
  const { page, taille } = pagination;

  const nouvelleDemande = useDerniereDemande();
  const lire = useCallback(async () => {
    const perimee = nouvelleDemande();
    setChargement(true);
    try {
      const params = { page, page_size: taille, q: termes.trim(), tri };
      if (filtre && filtreActif) params[filtre.param] = true;
      const resultat = await charger(params);
      if (perimee()) return;
      setDonnees(resultat);
      setErreur('');
    } catch (e) {
      if (perimee()) return;
      setErreur(messageErreur(e, 'Ce tableau n’a pas pu être chargé.'));
    } finally {
      if (!perimee()) setChargement(false);
    }
    // `charger` et `filtre` sont stables pour un onglet donné.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, taille, termes, tri, filtreActif, rafraichir, nouvelleDemande]);
  useEffect(() => {
    lire();
  }, [lire]);
  useRafraichirSur(domaines, lire);

  const totaux = donnees?.totals || {};
  const devise = donnees?.currency || 'XOF';
  const visibles = colonnes.filter((colonne) => !colonne.masquee?.(totaux));
  const lignes = donnees?.items || [];
  const nbColonnes = visibles.length + 1 + (action ? 1 : 0);

  return (
    <section className="space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-80">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input type="search" aria-label={rechercheLibelle} placeholder={rechercheLibelle} value={recherche} onChange={(e) => changer(`${cle}-q`, e.target.value, true)} className="h-8 pl-8 text-sm" />
        </div>
        {filtre && (
          <label className="inline-flex h-8 items-center gap-2 rounded-full border bg-card px-3 text-xs font-medium">
            <input type="checkbox" className="h-3.5 w-3.5" checked={filtreActif} onChange={(e) => changer(`${cle}-tout`, e.target.checked ? '' : '1')} />
            {filtre.libelle}
          </label>
        )}
        {barre}
      </div>

      {erreur && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 p-3 text-sm text-destructive">
          <span>{erreur}</span>
          <Button variant="outline" size="sm" className="h-7" onClick={lire}>Réessayer</Button>
        </div>
      )}
      {!donnees && !erreur && <LoadingState shape="table" rows={8} columns={6} label="Chargement du tableau" />}
      {donnees && (
        <div className={cn('overflow-x-auto rounded-xl border bg-card', chargement && 'opacity-70')}>
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <EnTete titre={nom.titre} tri={nom.tri} triActuel={tri} onTrier={(valeur) => changer(`${cle}-tri`, valeur === triParDefaut ? '' : valeur)} />
                {visibles.map((colonne) => (
                  <EnTete
                    key={colonne.cle}
                    titre={colonne.titre}
                    tri={colonne.tri}
                    triActuel={tri}
                    droite={colonne.montant || colonne.droite}
                    className="hidden sm:table-cell"
                    onTrier={(valeur) => changer(`${cle}-tri`, valeur === triParDefaut ? '' : valeur)}
                  />
                ))}
                {montantMobile && <th className="px-3 py-2 text-right font-semibold sm:hidden">{montantMobile.titre}</th>}
                {action && <th className="hidden px-3 py-2 sm:table-cell"><span className="sr-only">Action</span></th>}
              </tr>
            </thead>
            <tbody>
              {lignes.length === 0 && (
                <tr className="h-10">
                  <td colSpan={nbColonnes + 1} className="px-3 py-2 text-muted-foreground">
                    {recherche ? 'Aucune ligne ne correspond.' : vide}
                    {!recherche && filtre && filtreActif && (
                      <button type="button" className="ml-2 font-medium text-primary hover:underline" onClick={() => changer(`${cle}-tout`, '1')}>Tout voir</button>
                    )}
                  </td>
                </tr>
              )}
              {lignes.map((ligne) => (
                <tr
                  key={identifiant(ligne)}
                  className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                  onClick={() => onOuvrir(ligne)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOuvrir(ligne); }}
                >
                  <td className="w-full max-w-0 px-3 py-1.5">
                    <span className="block truncate font-medium" title={nom.rendu(ligne)}>{nom.rendu(ligne)}</span>
                    {detailMobile && <span className="block truncate text-xs text-muted-foreground sm:hidden">{detailMobile(ligne)}</span>}
                  </td>
                  {visibles.map((colonne) => (
                    <td key={colonne.cle} className={cn('hidden whitespace-nowrap px-3 py-1.5 sm:table-cell', (colonne.montant || colonne.droite) && 'text-right tabular-nums', colonne.classe)}>
                      {colonne.rendu ? colonne.rendu(ligne, devise) : colonne.montant ? montantOuVide(ligne[colonne.cle], devise) : ligne[colonne.cle]}
                    </td>
                  ))}
                  {montantMobile && <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums sm:hidden">{montantOuVide(ligne[montantMobile.cle], devise)}</td>}
                  {action && (
                    // Sur téléphone, le geste est dans le panneau ouvert par la ligne.
                    <td className="hidden whitespace-nowrap px-3 py-1.5 text-right sm:table-cell" onClick={(e) => e.stopPropagation()}>
                      {action(ligne)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {lignes.length > 0 && (
              <tfoot className="border-t bg-muted/50 font-semibold" data-testid={testid ? `${testid}-total` : undefined}>
                <tr>
                  <td className="px-3 py-2">{totalLibelle(donnees.total)}</td>
                  {visibles.map((colonne) => (
                    <td key={colonne.cle} className={cn('hidden whitespace-nowrap px-3 py-2 sm:table-cell', (colonne.montant || colonne.droite) && 'text-right tabular-nums')}>
                      {colonne.montant ? formatMontant(totaux[colonne.cle] || 0, devise) : colonne.total ? colonne.total(totaux) : ''}
                    </td>
                  ))}
                  {montantMobile && <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums sm:hidden">{formatMontant(totaux[montantMobile.cle] || 0, devise)}</td>}
                  {action && <td className="hidden sm:table-cell" />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      <BarrePagination pagination={pagination} nom="lignes" />
    </section>
  );
}

const montantOuVide = (valeur, devise) => (valeur == null || Number(valeur) === 0 ? '' : formatMontant(valeur, devise));

const EnTete = ({ titre, tri, triActuel, droite, className, onTrier }) => (
  <th className={cn('px-3 py-2 font-semibold', droite && 'text-right', className)} aria-sort={tri && triActuel === tri ? 'descending' : undefined}>
    {tri ? (
      <button type="button" className={cn('inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground', triActuel === tri && 'text-foreground')} onClick={() => onTrier(tri)}>
        {titre}
        {triActuel === tri && <ArrowDown className="h-3 w-3" aria-hidden="true" />}
      </button>
    ) : titre}
  </th>
);

/** « il y a 12 j » depuis un nombre de jours: une ancienneté, jamais un retard. */
export const ilYA = (jours) => {
  if (jours == null || !Number.isFinite(Number(jours))) return '';
  const n = Number(jours);
  return n <= 0 ? 'aujourd’hui' : `il y a ${formatNombre(n)} j`;
};
